import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from './common/Catalyst/dialog';
import { Button } from './common/Catalyst/button';
import { Text } from './common/Catalyst/text';
import LoadingSpinner from './LoadingSpinner';
import { bankService } from './clients/ApiClients';
import { NsfItemReturn } from './clients/rust-api-client';
import { formatCurrency, getBankErrorParts } from './utils/bankHelpers';
import { nsfItemLabel } from './utils/nsfHelpers';

interface NsfImageDialogProps {
  /** The POD item whose check to show; `null` closes the dialog. */
  item: NsfItemReturn | null;
  connectionId: string;
  onClose: () => void;
}

type Side = 'front' | 'back';

/**
 * Front and back of the check behind an NSF item, fetched live from the
 * bank. The image endpoint takes the check's own id, which the API
 * resolves through the bank's Check endpoints — so each side is two
 * bank reads, and a miss means the bank has no matching check.
 */
const NsfImageDialog: React.FC<NsfImageDialogProps> = ({
  item,
  connectionId,
  onClose,
}) => {
  const [side, setSide] = useState<Side>('front');
  const [urls, setUrls] = useState<Partial<Record<Side, string>>>({});
  const [loading, setLoading] = useState<Side | null>(null);
  const [error, setError] = useState<string | null>(null);

  // One object URL per side, revoked when the item changes or the
  // dialog unmounts.
  useEffect(() => {
    setSide('front');
    setUrls({});
    setError(null);
    return () => {
      setUrls((current) => {
        Object.values(current).forEach((u) => u && URL.revokeObjectURL(u));
        return {};
      });
    };
  }, [item]);

  useEffect(() => {
    if (!item || urls[side] || loading === side) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(side);
        setError(null);
        const resp = await bankService.getConnectionNsfImage(
          { connectionId, id: item.id, side },
          { responseType: 'blob' as const }
        );
        const blob = resp.data as unknown as Blob;
        const url = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        setUrls((current) => ({ ...current, [side]: url }));
      } catch (e) {
        if (cancelled) return;
        const { status, body } = getBankErrorParts(e);
        setError(
          status === 404
            ? body || 'The bank has no image for this item.'
            : body || "Couldn't load the check image."
        );
      } finally {
        if (!cancelled) setLoading(null);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
    // `urls` and `loading` are read, not depended on: adding them would
    // re-run the effect on every state change this effect itself makes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, side, connectionId]);

  const url = urls[side];

  return (
    <Dialog open={!!item} onClose={onClose} size="3xl">
      <DialogTitle>{item ? nsfItemLabel(item) : 'Check'}</DialogTitle>
      <DialogDescription>
        {item?.amount ? formatCurrency(Number(item.amount)) : ''} on account{' '}
        {item?.account_number ?? '—'}. Live from the bank.
      </DialogDescription>
      <DialogBody>
        <div className="mb-3 flex gap-2">
          {(['front', 'back'] as Side[]).map((s) =>
            side === s ? (
              <Button key={s} outline onClick={() => setSide(s)}>
                {s === 'front' ? 'Front' : 'Back'}
              </Button>
            ) : (
              <Button key={s} plain onClick={() => setSide(s)}>
                {s === 'front' ? 'Front' : 'Back'}
              </Button>
            )
          )}
        </div>
        <div className="min-h-48 rounded-lg bg-stone-100 p-2 dark:bg-gray-700">
          {loading === side && !url ? (
            <LoadingSpinner />
          ) : url ? (
            <img
              src={url}
              alt={`${side} of ${item ? nsfItemLabel(item) : 'the check'}`}
              className="mx-auto max-h-[70vh] w-auto"
            />
          ) : (
            <Text className="p-4 text-sm">{error ?? 'No image.'}</Text>
          )}
        </div>
      </DialogBody>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default NsfImageDialog;
